import React, {
  ChangeEvent,
  memo,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Avatar,
  Box,
  Button,
  IconButton,
  Input,
  Typography,
} from '@mui/material';
import { convertToBase64 } from 'src/utils';

import {
  BannerSelectPlaceholder,
  CloseIcon,
  GalleryMenuIcon,
  PenMenuIcon,
  UploadAvatarIcon,
} from '../../Icons';

interface FloatingImageButtonProps {
  children: any;
  onClick: () => void;
  sx?: any;
}

const FloatingImageButton = ({
  children,
  onClick,
  sx,
  ...rest
}: FloatingImageButtonProps & any) => {
  return (
    <IconButton
      sx={{
        position: 'absolute',
        display: 'flex',
        bottom: '16px',
        right: '40px',
        height: '30px',
        width: '164px',
        alignItems: 'center',
        backgroundColor: 'white',
        fontSize: '16px',
        color: '#BF295C',
        borderRadius: '6px',
        boxShadow: '0px 4px 16px 0px rgba(41, 66, 90, 0.12)',

        ':hover': {
          backgroundColor: '#FAFAFA',
        },

        ':active': {
          boxShadow: 'none',
          border: '1px solid #F3F3F3',
          backgroundColor: '#FAFAFA',
        },
      }}
      disableRipple
      onClick={onClick}
      {...rest}
    >
      {children}
    </IconButton>
  );
};

interface BannerSelectProps {
  onImageChanged: (image: string) => void;
  image: string;
  ref: any;
}

const BannerSelect = memo(React.forwardRef(
  ({ onImageChanged, image }: BannerSelectProps, ref) => {
    const placeholderImageSrc = '';

    const [imageSrc, setImageSrc] = useState<string>(
      image || placeholderImageSrc,
    );

    const isImageWasAdded = useMemo(() => {
      return imageSrc !== placeholderImageSrc;
    }, [imageSrc]);

    const fileInputRef = useRef<any>(null);
    const formRef = useRef<any>(null);

    const handleUploadImageButton = () => {
      fileInputRef?.current?.click();
    };

    const handleDeleteImage = () => {
      formRef?.current?.reset();
      setImageSrc(placeholderImageSrc);
      onImageChanged('');
    };

    const handleImageChange = async (e: any) => {
      if (e.target.files) {
        const base64 = await convertToBase64(e.target.files[0]);

        setImageSrc(base64);
        onImageChanged(base64);
      }
    };

    useImperativeHandle(ref, () => ({
      deleteImage: handleDeleteImage,
    }));

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          width: '100%',
          flex: 1,
          height: 238,
        }}
      >
        {isImageWasAdded ? (
          <Box>
            <img
              src={image}
              style={{
                width: '100%',
                height: 238,
                objectFit: 'cover',
              }}
            />
          </Box>
        ) : (
          <Box
            onClick={handleUploadImageButton}
            sx={{
              padding: 0,
              backgroundColor: '#f3f3f3',
              borderRadius: 0,
              border: 'none',
              cursor: 'pointer',
              boxShadow: 'none',
              width: '100%',
              height: 238,
            }}
          >
            <Box
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                flex: 1,
                justifyContent: 'center',
                width: '100%',
                height: '100%',
                color: '#B9B9B9',
                fontSize: '100px',
              }}
            >
              <BannerSelectPlaceholder fontSize="inherit" />
              <Typography
                sx={{
                  color: '#BF295C',
                  fontWeight: '400',
                  fontSize: '18px',
                }}
              >
                Add banner image
              </Typography>
            </Box>
          </Box>
        )}
        <form ref={formRef}>
          <input
            ref={fileInputRef}
            onChange={handleImageChange}
            style={{
              display: 'none',
            }}
            type="file"
            accept="image/*"
          />
          {isImageWasAdded ? (
            <FloatingImageButton onClick={handleDeleteImage}>
              <GalleryMenuIcon fontSize="inherit" />
              <Typography
                sx={{
                  fontWeight: '500',
                  fontSize: '14px',
                  color: '#080808',
                  marginLeft: '6px',
                }}
              >
                Edit banner image
              </Typography>
            </FloatingImageButton>
          ) : null}
        </form>
      </Box>
    );
  },
));

export {BannerSelect}
